import React, {
	ChangeEvent,
	KeyboardEvent,
	useMemo,
	useRef,
	useState,
} from 'react';

import { songs } from '@/assets/icons';
import { ChatInput } from '@/components/shared/Inputs';
import { SendMessageButton } from '@/components/shared/SendMessageButton';
import { ChatType, MessageType } from '@/graphql';
import { useThrottle } from '@/hooks/useThrottle';
import { useLayout } from '@/providers/LayoutProvider';
import { Capacitor } from '@capacitor/core';
import { SvgIcon } from '@material-ui/core';

import { ChatContainerSendMessageFn } from '../../ChatContainer';
import styles from './ChatBar.module.scss';
import { MentionMember, MentionPicker } from './MentionPicker';

export type AllowedMessageTypes = Exclude<MessageType, MessageType.Text>;

export type ChatbarCommonMessageTypes = (
	| {
			type: AllowedMessageTypes;
			onOpened?: () => void;
			onFinished?: (data: any) => void;
	  }
	| AllowedMessageTypes
)[];

export interface ChatbarProps {
	onMessageSend: ChatContainerSendMessageFn;
	onMessageInputThrottled?: (message: string) => void;
	isSending?: boolean;
	padding?: 'normal' | 'increased';
	commentingDisabled?: boolean;
	commonMessageTypes?: ChatbarCommonMessageTypes;
	hidden?: boolean;
	tralingComponent?: React.ReactElement;
	members?: Array<{ id: string; displayName?: string | null }>;
	chatType?: string;
}

const isCapacitor = Capacitor.isNativePlatform();

export const Chatbar = ({
	onMessageSend,
	onMessageInputThrottled,
	isSending = false,
	padding,
	commentingDisabled = false,
	commonMessageTypes,
	hidden = false,
	tralingComponent,
	members,
	chatType,
}: ChatbarProps) => {
	const { openSonglistWithCallback } = useLayout();
	const [value, setValue] = useState<string>('');
	const [mentionQuery, setMentionQuery] = useState<string | null>(null);
	const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	const filteredMembers = useMemo(() => {
		if (mentionQuery === null) return [];
		return (members ?? []).filter((m) => {
			const name = m.displayName ?? '';
			return name.toLowerCase().includes(mentionQuery.toLowerCase());
		});
	}, [mentionQuery, members]);

	const onMessageInput = useThrottle((message: string) => {
		onMessageInputThrottled && onMessageInputThrottled(message);
	}, 2000);

	// useThrottleFn<void, string[]>(
	// 	() => {
	// 		onMessageInputThrottled && onMessageInputThrottled(value);
	// 	},
	// 	2000,
	// 	[value],
	// );

	function extractMentionQuery(textBeforeCursor: string): string | null {
		// Check for @ trigger at end of text-before-cursor (capture group avoids lookbehind — unsupported iOS < 16.4)
		const match = textBeforeCursor.match(/(^|\s)@([^\s]*)$/);
		return match ? match[2] : null;
	}

	function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
		if (event.key === 'Escape' && mentionQuery !== null) {
			setMentionQuery(null);
			return;
		}
		if (filteredMembers.length > 0) {
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				setMentionActiveIndex((i) => (i + 1) % filteredMembers.length);
				return;
			}
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				setMentionActiveIndex(
					(i) =>
						(i - 1 + filteredMembers.length) %
						filteredMembers.length,
				);
				return;
			}
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				handleMemberSelect(filteredMembers[mentionActiveIndex]);
				return;
			}
		}
		if (!isCapacitor && event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			if (value !== '') {
				handleSubmit();
			}
		}
	}

	function handleSubmit() {
		const prevValue = value;
		const escapedNames = (members ?? [])
			.map((m) => m.displayName ?? '')
			.filter(Boolean)
			.sort((a, b) => b.length - a.length)
			.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		const mentionRegex =
			escapedNames.length > 0
				? new RegExp(`@(?:${escapedNames.join('|')})|@\\S+`, 'g')
				: /@\S+/g;
		const tokens = value.match(mentionRegex) ?? [];
		const mentionedUserIds = tokens
			.map((token) => {
				const name = token.slice(1);
				return (members ?? []).find((m) => m.displayName === name)?.id;
			})
			.filter((id): id is string => id !== undefined);
		// TODO(ifko): server reads this to fire push notifications
		const metadata =
			mentionedUserIds.length > 0
				? JSON.stringify({ mentionedUserIds })
				: undefined;
		inputRef.current?.focus();
		setValue('');
		setMentionQuery(null);
		onMessageSend(value, undefined, metadata).catch(() => {
			setValue(prevValue);
		});
	}

	function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
		const content = event.target.value;
		const cursor = event.target.selectionStart ?? content.length;
		setValue(content);
		onMessageInput(content);
		setMentionQuery(extractMentionQuery(content.slice(0, cursor)));
		setMentionActiveIndex(0);
	}

	function handleMemberSelect(member: MentionMember) {
		const name = member.displayName ?? '';
		const cursor = inputRef.current?.selectionStart ?? value.length;
		const before = value.slice(0, cursor);
		const after = value.slice(cursor);
		// Replace the @query fragment immediately before the cursor
		const newBefore = before.replace(/(^|\s)@[^\s]*$/, `$1@${name} `);
		setValue(newBefore + after);
		setMentionQuery(null);
		inputRef.current?.focus();
	}

	function renderCommonMessageTypes(
		commonMessageTypes?: ChatbarCommonMessageTypes,
	) {
		if (!commonMessageTypes) {
			return null;
		}

		return commonMessageTypes.map((messageType) => {
			const isObject = typeof messageType !== 'string';
			const noOp = () => void 0;
			// Get the type of the Extra component
			const type = isObject ? messageType.type : messageType;

			// Get the callback of the Extra component
			const onOpened = isObject ? messageType.onOpened || noOp : noOp;
			const onFinished = isObject ? messageType.onFinished || noOp : noOp;

			if (type === MessageType.Songlist) {
				return (
					<div
						key={`chatbar-${type}`}
						className={styles['chatbar__songlist']}
						onClick={() => {
							onOpened();
							openSonglistWithCallback((songIds) => {
								if (songIds.length) {
									onMessageSend(
										'Shared songs',
										MessageType.Songlist,
										JSON.stringify(songIds),
									);
									onFinished(songIds);
								}
							});
						}}
					>
						<SvgIcon component={songs} />
					</div>
				);
			}
		});
	}

	const isGroup = chatType === ChatType.Group;

	const formClasses = [
		styles['chatbar__form'],
		padding === 'increased'
			? styles['chatbar__form--increased-padding']
			: '',
		hidden && styles['hidden'],
	].filter(Boolean);

	return (
		<div className={formClasses.join(' ')}>
			{renderCommonMessageTypes(commonMessageTypes)}
			{tralingComponent}
			<div
				className={styles['chatbar__input']}
				style={{ position: 'relative' }}
			>
				{isGroup && filteredMembers.length > 0 && (
					<MentionPicker
						members={filteredMembers}
						activeIndex={mentionActiveIndex}
						onSelect={handleMemberSelect}
					/>
				)}
				<ChatInput
					inputRef={inputRef}
					id="chat-input"
					placeholder={
						commentingDisabled
							? 'Commenting disabled'
							: 'Add a message'
					}
					value={value}
					onKeyPress={handleKeyDown}
					onKeyDown={handleKeyDown}
					onChange={handleInputChange}
					disabled={commentingDisabled}
					transparent={commentingDisabled}
				/>
			</div>
			<div>
				<SendMessageButton
					onClick={handleSubmit}
					disabled={value === '' || isSending}
				/>
			</div>
		</div>
	);
};
